import React from 'react';
import PropTypes from 'prop-types';
import { 
  CardMedia, 
  Typography, 
  Box,
  Tooltip 
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import PendingIcon from '@mui/icons-material/Pending';
import { getEntityImageUrl } from '../../../../api/utils';
import { defaultGroupImage } from '../../../../assets/images/defaults';
import {
  StyledCard,
  StatusBadge,
  VisibilityBadge,
  ImageContainer,
  StatsRow,
  StatItem
} from './styled';

const STATUS_LABELS = {
  AC: 'Active',
  PP: 'Pending',
  IN: 'Inactive',
  RE: 'Rejected',
  BA: 'Banned',
};

const VISIBILITY_ICONS = {
  PU: <PublicIcon sx={{ fontSize: 14 }} />,
  PR: <LockIcon sx={{ fontSize: 14 }} />,
  SE: <VisibilityOffIcon sx={{ fontSize: 14 }} />,
};

const VISIBILITY_LABELS = {
  PU: 'Public',
  PR: 'Private',
  SE: 'Secret',
};

const CommunityListItem = ({ community, selectCommunity }) => {
  const handleImageRenderError = (e) => {
    e.target.src = defaultGroupImage;
  };

  const memberCount = community?.member_count || 0;
  const venueCount = community?.venue_count || 0;

  return (
    <Box onClick={() => selectCommunity(community)} sx={{ cursor: 'pointer', p: 1 }}>
      <StyledCard>
        <StatusBadge status={community?.status}>
          {community?.status === 'PP' && <PendingIcon sx={{ fontSize: 14 }} />}
          {STATUS_LABELS[community?.status] || community?.status}
        </StatusBadge>
        
        <Tooltip title={VISIBILITY_LABELS[community?.visibility] || 'Unknown'}>
          <VisibilityBadge visibility={community?.visibility}>
            {VISIBILITY_ICONS[community?.visibility]}
            {VISIBILITY_LABELS[community?.visibility]}
          </VisibilityBadge>
        </Tooltip>
        
        <ImageContainer>
          <CardMedia
            component="img"
            src={community?.image || getEntityImageUrl(community, 'thumbnail', defaultGroupImage)}
            alt={community?.name}
            onError={handleImageRenderError}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </ImageContainer>

        <Box sx={{ p: 2, pb: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '1.1rem',
              fontWeight: 500,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              mb: 0.5
            }}
          >
            {community?.name}
          </Typography>
          
          {community?.description && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                mb: 1,
                minHeight: '2.5em'
              }}
            >
              {community?.description}
            </Typography>
          )}
          
          <StatsRow>
            <Tooltip title="Members">
              <StatItem>
                <PeopleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <span>{memberCount}</span>
              </StatItem>
            </Tooltip>
            
            {venueCount > 0 && (
              <Tooltip title="Venue Partnerships">
                <StatItem>
                  <BusinessIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                  <span>{venueCount}</span>
                </StatItem>
              </Tooltip>
            )}
          </StatsRow>
        </Box>
      </StyledCard>
    </Box>
  );
};

CommunityListItem.propTypes = {
  community: PropTypes.object.isRequired,
  selectCommunity: PropTypes.func.isRequired,
};

export default CommunityListItem;
